import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { FaEllipsisV } from "react-icons/fa";
import { request } from "../../../utils/api";
import { toast } from "react-toastify";
import Loading from "../utilComponent/Loading";
import ViewUserModal from "../modal/ViewUserModal";
import DeleteModal from "../modal/DeleteModal";
import Pagination from "../utilComponent/Pagination";

const WarehouseProducts = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(id || "");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 12,
    total: 0,
    last_page: 1,
  });

  const getProductWarehouse = async () => {
    try {
      const response = await request({
        url: "warehouses",
        method: "GET",
      });

      if (response?.data?.items) {
        setWarehouses(
          response.data.items.map((item) => ({
            warehouse_name: item.name,
            id: item.id,
          }))
        );
      }
    } catch (error) {
      toast.error("Error fetching warehouses");
    }
  };

  const getWarehouseProducts = async (page = 1) => {
    if (!selectedWarehouse) return;

    try {
      setLoading(true);
      const response = await request({
        url: `products?page=${page}&per_page=${pagination.per_page}&warehouse_id=${selectedWarehouse}`,
        method: "GET",
      });

      if (response.status && response.data) {
        setProducts(response.data.items);
        setPagination(response.data.pagination);
      }
    } catch (err) {
      toast.error("Error fetching products");
    } finally {
      setLoading(false);
    }
  };

  const deleteProduct = async () => {
    if (!selectedProduct) return;

    try {
      setLoading(true);
      await request({
        url: `products/${selectedProduct.id}`,
        method: "DELETE",
      });

      toast.success("Product deleted successfully");
      setShowDeleteModal(false);
      setSelectedProduct(null);
      getWarehouseProducts(pagination.current_page);
    } catch {
      toast.error("Cannot delete product");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProductWarehouse();
  }, []);

  useEffect(() => {
    if (selectedWarehouse) {
      getWarehouseProducts(1);
      setPagination((prev) => ({ ...prev, current_page: 1 }));
    }
  }, [selectedWarehouse]);

  const handlePageChange = (page) => {
    setPagination({ ...pagination, current_page: page });
    getWarehouseProducts(page);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value.toLowerCase());
  };

  const handleView = (product) => {
    setSelectedProduct(product);
    setShowViewModal(true);
  };

  const handleEdit = (product) => {
    navigate(`/product-update/${product.id}`);
  };

  const handleDelete = (product) => {
    setSelectedProduct(product);
    setShowDeleteModal(true);
  };

  const filteredProducts = products.filter((p) =>
    Object.values(p).some((v) =>
      String(v).toLowerCase().includes(searchText)
    )
  );

  return (
    <div className="page-body px-xl-4 px-sm-2 px-0 py-3">
      <div className="container-fluid">
        <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
                <ol className="breadcrumb bg-transparent mb-0">
                  <li className="breadcrumb-item">
                    <Link to="/">Home</Link>
                  </li>
                  <li className="breadcrumb-item">Warehouse</li>
                  <li className="breadcrumb-item active">Warehouse Products</li>
                </ol>
              </div>

        <div className="card shadow-sm">
          <div className="card-header d-flex justify-content-between align-items-center gap-2">
            <h6 className="m-0">Products in Warehouse</h6>
            <input
              className="form-control w-25"
              placeholder="Search product..."
              onChange={handleSearch}
            />
          </div>
          <div className="m-3 d-flex justify-content-start">
           <select
              className="form-select w-25"
              value={selectedWarehouse}
              onChange={(e) => setSelectedWarehouse(e.target.value)}
            >
              <option value="">Select Warehouse</option>
              {warehouses.map((wh) => (
                <option key={wh.id} value={wh.id}>
                  {wh.warehouse_name}
                </option>
              ))}
            </select>
          </div>

          <div className="card-body">
            {loading ? (
              <Loading />
            ) : (
              <div className="row">
                {filteredProducts.length ? (
                  filteredProducts.map((p) => (
                    <div className="col-xl-3 col-lg-4 col-md-6 mb-4" key={p.id}>
                      <div className="card h-100 shadow-sm">
                        {p.image && (
                          <img
                            src={p.image}
                            alt={p.name}
                            className="card-img-top"
                            style={{ objectFit: "cover", height: "150px" }}
                          />
                        )}
                        <div className="card-body">
                          <div className="d-flex justify-content-between">
                            <h6 className="fw-bold mb-1">{p.name}</h6>
                            <Dropdown align="end">
                              <Dropdown.Toggle
                                variant="light"
                                className="border-0 p-0"
                              >
                                <FaEllipsisV />
                              </Dropdown.Toggle>

                              <Dropdown.Menu>
                                <Dropdown.Item onClick={() => handleView(p)}>
                                  View
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => handleEdit(p)}>
                                  Edit
                                </Dropdown.Item>
                                <Dropdown.Item
                                  className="text-danger"
                                  onClick={() => handleDelete(p)}
                                >
                                  Delete
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>

                          <p className="mb-1">
                            <strong>SKU:</strong> {p.sku}
                          </p>
                          <p className="mb-1">
                            <strong>Qty:</strong> {p.warehouseQty}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-5">
                    No products found in this warehouse
                  </div>
                )}
              </div>
            )}

            {pagination.last_page > 1 && (
              <div className="d-flex justify-content-center mt-4">
                <Pagination
                  currentPage={pagination.current_page}
                  lastPage={pagination.last_page}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>

        <ViewUserModal
          show={showViewModal}
          onClose={() => setShowViewModal(false)}
          data={selectedProduct}
          fields={[
            { label: "Name", key: "name" },
            { label: "SKU", key: "sku" },
            { label: "Quantity", key: "warehouseQty" },
            { label: "Type", key: "type" },
            { label: "Price", key: "price" },
          ]}
          title="Product Details"
          onEdit={(p) => navigate(`/product-update/${p.id}`)}
        />

        <DeleteModal
          show={showDeleteModal}
          title="Delete Product"
          message={`Are you sure you want to delete "${selectedProduct?.name}"?`}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={deleteProduct}
        />
      </div>
    </div>
  );
};

export default WarehouseProducts;
